import React, { useEffect, useRef, useState } from "react";
import { useLanguage } from "../../i18n";
import { resolveUrl } from "../../api";

declare global {
  interface Window {
    google?: any;
    __gmapsLoadPromise?: Promise<void>;
  }
}

export function loadGoogleMaps(apiKey: string): Promise<void> {
  if (window.google?.maps) return Promise.resolve();
  if (window.__gmapsLoadPromise) return window.__gmapsLoadPromise;
  window.__gmapsLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Google Maps"));
    document.head.appendChild(script);
  });
  return window.__gmapsLoadPromise;
}

export interface MapMarker {
  id: string;
  lat: number;
  lng: number;
  title: string;
  subtitle?: string;
  price?: string;
  photo?: string;
}

// A price-bubble marker rendered as plain HTML (OverlayView), so it works on
// the classic Maps JS API without needing a Map ID / Advanced Markers setup.
function makePriceMarkerClass(gmaps: any) {
  return class PriceMarker extends gmaps.OverlayView {
    position: any;
    price: string;
    onClick: () => void;
    div: HTMLDivElement | null = null;

    constructor(position: { lat: number; lng: number }, price: string, onClick: () => void) {
      super();
      this.position = position;
      this.price = price;
      this.onClick = onClick;
    }

    onAdd() {
      const div = document.createElement("div");
      div.className = "map-price-pin";
      div.textContent = this.price;
      div.addEventListener("click", (e) => {
        e.stopPropagation();
        this.onClick();
      });
      this.div = div;
      this.getPanes().overlayMouseTarget.appendChild(div);
    }

    draw() {
      const proj = this.getProjection();
      if (!proj || !this.div) return;
      const pos = proj.fromLatLngToDivPixel(new gmaps.LatLng(this.position.lat, this.position.lng));
      this.div.style.left = `${pos.x}px`;
      this.div.style.top = `${pos.y}px`;
    }

    onRemove() {
      if (this.div?.parentNode) this.div.parentNode.removeChild(this.div);
      this.div = null;
    }
  };
}

function buildInfoContent(m: MapMarker, onView: () => void, viewDetailsLabel: string): HTMLElement {
  const wrap = document.createElement("div");
  wrap.style.cssText = "max-width:200px;padding:2px;";
  if (m.photo) {
    const img = document.createElement("img");
    img.src = resolveUrl(m.photo);
    img.style.cssText = "width:100%;height:100px;object-fit:cover;border-radius:8px;margin-bottom:8px;display:block;";
    wrap.appendChild(img);
  }
  const title = document.createElement("div");
  title.style.cssText = "font-weight:800;font-size:13.5px;margin-bottom:2px;color:var(--ink,#111);";
  title.textContent = m.title;
  wrap.appendChild(title);

  if (m.subtitle) {
    const sub = document.createElement("div");
    sub.style.cssText = "font-size:11.5px;color:var(--ink-faint,#888);margin-bottom:6px;";
    sub.textContent = m.subtitle;
    wrap.appendChild(sub);
  }
  if (m.price) {
    const price = document.createElement("div");
    price.style.cssText = "font-weight:800;font-size:12.5px;color:var(--brand-700,#0f172a);margin-bottom:8px;";
    price.textContent = m.price;
    wrap.appendChild(price);
  }
  const btn = document.createElement("button");
  btn.type = "button";
  btn.textContent = viewDetailsLabel;
  btn.style.cssText =
    "background:var(--brand-700,#111827);color:#fff;border:none;border-radius:8px;padding:8px 12px;font-size:12px;font-weight:700;cursor:pointer;width:100%;";
  btn.addEventListener("click", onView);
  wrap.appendChild(btn);
  return wrap;
}

export function GoogleMapView({
  apiKey,
  markers,
  me,
  searchCenter,
  onMarkerClick,
  height = 380,
  mode = "listings",
}: {
  apiKey: string;
  markers: MapMarker[];
  me?: { lat: number; lng: number } | null;
  // A place the user searched for (see the map-search box in Browse) —
  // distinct from `me` (device GPS): shown as its own flag pin and pulls
  // the map's bounds to that area even when no listing happens to be
  // pinned nearby yet.
  searchCenter?: { lat: number; lng: number; label?: string } | null;
  onMarkerClick?: (id: string) => void;
  height?: number | string;
  // "listings" = clickable price-bubble pins with an info window (Browse).
  // "pin" = a single plain, non-interactive pin (location confirmation).
  mode?: "listings" | "pin";
}) {
  const { t } = useLanguage();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const infoWindowRef = useRef<any>(null);
  const markerObjsRef = useRef<any[]>([]);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps(apiKey)
      .then(() => {
        if (cancelled || !containerRef.current) return;
        mapRef.current = new window.google.maps.Map(containerRef.current, {
          center: { lat: 20.5937, lng: 78.9629 },
          zoom: 5,
          disableDefaultUI: true,
          zoomControl: true,
        });
        infoWindowRef.current = new window.google.maps.InfoWindow();
        setReady(true);
      })
      .catch(() => !cancelled && setError(t("googleMap.couldNotLoad")));
    return () => {
      cancelled = true;
    };
  }, [apiKey]);

  useEffect(() => {
    if (!ready || !mapRef.current) return;
    const gmaps = window.google.maps;
    const PriceMarker = makePriceMarkerClass(gmaps);
    markerObjsRef.current.forEach((m) => m.setMap(null));
    markerObjsRef.current = [];

    const bounds = new gmaps.LatLngBounds();
    let any = false;

    markers.forEach((m) => {
      const marker =
        mode === "pin"
          ? new gmaps.Marker({ position: { lat: m.lat, lng: m.lng }, map: mapRef.current, title: m.title })
          : new PriceMarker({ lat: m.lat, lng: m.lng }, m.price ?? m.title, () => {
              const iw = infoWindowRef.current;
              iw.setContent(buildInfoContent(m, () => onMarkerClick?.(m.id), t("googleMap.viewDetails")));
              iw.setPosition({ lat: m.lat, lng: m.lng });
              iw.open(mapRef.current);
            });
      if (mode !== "pin") marker.setMap(mapRef.current);
      markerObjsRef.current.push(marker);
      bounds.extend({ lat: m.lat, lng: m.lng });
      any = true;
    });

    if (me) {
      const meMarker = new gmaps.Marker({
        position: { lat: me.lat, lng: me.lng },
        map: mapRef.current,
        title: t("googleMap.yourLocation"),
        icon: {
          path: gmaps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: "#2563eb",
          fillOpacity: 1,
          strokeColor: "#fff",
          strokeWeight: 2,
        },
        zIndex: 999,
      });
      markerObjsRef.current.push(meMarker);
      bounds.extend({ lat: me.lat, lng: me.lng });
      any = true;
    }

    if (searchCenter) {
      const searchMarker = new gmaps.Marker({
        position: { lat: searchCenter.lat, lng: searchCenter.lng },
        map: mapRef.current,
        title: searchCenter.label ?? t("googleMap.searchedLocation"),
        icon: {
          path: gmaps.SymbolPath.CIRCLE,
          scale: 9,
          fillColor: "#F0653A",
          fillOpacity: 1,
          strokeColor: "#fff",
          strokeWeight: 2,
        },
        zIndex: 999,
      });
      markerObjsRef.current.push(searchMarker);
      // A fresh search should feel like it actually moved the map there —
      // bounds alone would only nudge it if the point happened to fall
      // outside the current view, so this always re-centers first and lets
      // fitBounds below widen out from there to also fit nearby listings.
      mapRef.current.setCenter({ lat: searchCenter.lat, lng: searchCenter.lng });
      bounds.extend({ lat: searchCenter.lat, lng: searchCenter.lng });
      any = true;
    }

    if (any) {
      mapRef.current.fitBounds(bounds);
      gmaps.event.addListenerOnce(mapRef.current, "bounds_changed", () => {
        if (mapRef.current.getZoom() > 15) mapRef.current.setZoom(15);
      });
    }
  }, [ready, markers, me, searchCenter, onMarkerClick, mode, t]);

  if (error) return <div className="empty-state">{error}</div>;

  return (
    <div
      ref={containerRef}
      style={{ height, width: "100%", borderRadius: 14, background: "var(--surface-alt, #eee)" }}
    />
  );
}

// A fixed center pin that the owner locates by panning the MAP underneath
// it, rather than dragging a small marker — a lot easier to land precisely
// on a phone. `resetKey` should change only when the caller wants to jump
// the map to a brand-new center (e.g. a fresh address search or GPS fix);
// changing it remounts this component, which is what actually moves the
// map — panning it here never touches resetKey, so it can't fight the user.
export function MapPinPicker({
  apiKey,
  center,
  onSettled,
  height = 200,
  rounded = true,
}: {
  apiKey: string;
  center: { lat: number; lng: number };
  onSettled: (lat: number, lng: number) => void;
  height?: number | string;
  rounded?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState("");
  const { t } = useLanguage();

  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps(apiKey)
      .then(() => {
        if (cancelled || !containerRef.current) return;
        const map = new window.google.maps.Map(containerRef.current, {
          center,
          zoom: 17,
          disableDefaultUI: true,
          zoomControl: true,
          gestureHandling: "greedy",
        });
        // Maps fires "idle" once tiles finish loading even with no user
        // interaction — comparing against the seeded center (rather than
        // just skipping "the first idle") skips that spurious event
        // reliably no matter how slow tile-loading is or how many idle
        // events land before the map is actually ready.
        map.addListener("idle", () => {
          const c = map.getCenter();
          const lat = c.lat();
          const lng = c.lng();
          if (Math.abs(lat - center.lat) < 1e-9 && Math.abs(lng - center.lng) < 1e-9) return;
          onSettled(lat, lng);
        });
      })
      .catch(() => !cancelled && setError(t("googleMap.couldNotLoad")));
    return () => {
      cancelled = true;
    };
    // Deliberately just [apiKey] — center/onSettled are only meant to seed
    // the initial map position; re-running this on every settle would fight
    // the pan the user is mid-gesture on. Use `resetKey` on the caller side
    // to force a fresh center instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey]);

  if (error) return <div className="empty-state">{error}</div>;

  return (
    <div style={{ position: "relative", height, width: "100%" }}>
      <div
        ref={containerRef}
        style={{ height: "100%", width: "100%", borderRadius: rounded ? 14 : 0, background: "var(--surface-alt, #eee)" }}
      />
      <div className="pin-picker-pin">📍</div>
    </div>
  );
}
